<template>
  <BaseForm class="edit-portfolio-form" v-bind="forward" @submit="onSubmit">
    <fieldset class="form-section">
      <legend>Share Your Website</legend>
      <WebsiteField v-model="website1" placeholder="https://">
        External Portfolio URL 1 <span class="optional">Optional</span>
      </WebsiteField>
      <WebsiteField v-model="website2" placeholder="https://" class="mt-24">
        External Portfolio URL 2 <span class="optional">Optional</span>
      </WebsiteField>
      <WebsiteField v-model="website3" placeholder="https://" class="mt-24">
        External Portfolio URL 3 <span class="optional">Optional</span>
      </WebsiteField>
    </fieldset>
    <PortfolioUploadField
      :portfolio="portfolio"
      :error="portfolioError"
      :max-new-items="maxNewItems"
      @add="onAdd"
      @remove="onRemove"
      @reorder="onReorder"
    />
    <template #actions><slot name="actions" /></template>
    <template #footer><slot name="footer" /></template>
  </BaseForm>
</template>

<script>
import { toInteger, toNonEmptyStringArray, toString } from '@/helpers/convert';
import { settings } from '@/helpers/portfolio';
import userForm from '@/mixins/userForm';
import Profile from '@/models/Profile';

export default {
  name: 'EditPortfolioForm',
  mixins: [userForm],
  props: {
    /** @type {import('vue/types/options').PropOptions<unknown>} */
    maxNewItems: {
      default: null,
    },
    /** @type {import('vue/types/options').PropOptions<string | null | undefined>} */
    verb: {
      type: String,
      default: 'Save Portfolio',
    },
  },
  data() {
    return {
      /** @type {PortfolioItem[]} */
      addedItems: [],
      /** @type {boolean} */
      itemsReordered: false,
      /** @type {string | null} */
      portfolioError: null,
      /** @type {PortfolioItem[]} */
      removedItems: [],
      /** @type {URL | null} */
      website1: null,
      /** @type {URL | null} */
      website2: null,
      /** @type {URL | null} */
      website3: null,
    };
  },
  computed: {
    isDirty: {
      /**
       * @returns {boolean}
       */
      get() {
        return !!(this.itemsChanged || this.websitesChanged);
      },
      /**
       * @param {boolean} value
       */
      set() {
        return;
      },
    },
    /**
     * @returns {boolean}
     */
    isOwnProfile() {
      const userId = this.$activeUser?.id;
      return !!(userId && userId === this.profile?.userId);
    },
    /**
     * @returns {boolean}
     */
    isUploading() {
      return !!(this.isOwnProfile && this.$store.getters['portfolios/isUploading']);
    },
    /**
     * @returns {boolean}
     */
    itemsChanged() {
      return this.newItemCount > 0 || this.removedItems.length > 0 || !!this.itemsReordered;
    },
    /**
     * @returns {number}
     */
    maxItemCount() {
      return settings.maxItemCount;
    },
    /**
     * @returns {number}
     */
    maxNewItemCount() {
      const prop = toInteger(this.maxNewItems, 0);
      return prop == null ? this.maxItemCount : Math.min(prop, this.maxItemCount);
    },
    /**
     * @returns {number}
     */
    minItemCount() {
      return settings.minItemCount;
    },
    /**
     * @returns {number}
     */
    newItemCount() {
      return this.addedItems.length;
    },
    /**
     * @returns {Portfolio | null}
     */
    portfolio() {
      /** @type {Profile | null} */
      const profile = (this.isUploading && this.$activeProfile) || this.profile;
      return profile && profile.portfolios;
    },
    /**
     * @returns {boolean}
     */
    websitesChanged() {
      /** @type {{ profile: Profile | null }} */
      const { profile } = this;
      return (
        !!profile &&
        (toString(this.website1) !== toString(profile.websites[0]) ||
          toString(this.website2) !== toString(profile.websites[1]) ||
          toString(this.website3) !== toString(profile.websites[2]))
      );
    },
  },
  methods: {
    /**
     * @param {number} count
     * @returns {string}
     */
    getItemCountText(count) {
      count = toInteger(count, 0) || null;
      return `${count} image${count === 1 ? '' : 's'}`;
    },
    /**
     * @param {PortfolioItem} items
     * @returns {void}
     */
    onAdd(item) {
      this.addedItems.push(item);
      if (this.newItemCount <= this.maxNewItemCount) {
        this.portfolioError = null;
      }
    },
    /**
     * @param {PortfolioItem} item
     * @returns {void}
     */
    onRemove(item) {
      const { addedItems } = this;
      const index = addedItems.indexOf(item);
      if (index >= 0 && index < addedItems.length) {
        addedItems.splice(index, 1);
      } else {
        this.removedItems.push(item);
      }
      this.portfolioError = null;
    },
    /**
     * @returns {void}
     */
    onReorder() {
      this.itemsReordered = true;
      this.portfolioError = null;
    },
    /**
     * @returns {Promise<void>}
     */
    async onSubmit() {
      /** @type {{ isUploading: boolean; liveChanges: boolean; portfolio: Portfolio | null; profile: Profile | null; user: User | null }} */
      const { isUploading, liveChanges, portfolio, profile, user } = this;
      if (!user || !portfolio || !profile || (!liveChanges && !this.isDirty)) {
        return;
      }

      // Validate portfolio items
      if (!isUploading) {
        this.portfolioError = null;
        const itemCount = portfolio.length;
        const min = this.minItemCount;
        if (itemCount < min) {
          this.portfolioError = `Please upload at least ${min} item${min === 1 ? '' : 's'}.`;
          return;
        }
        const excess = Math.max(
          0,
          itemCount - this.maxItemCount,
          this.newItemCount - this.maxNewItemCount
        );
        if (excess) {
          this.portfolioError = `Please remove ${excess} item${excess === 1 ? '' : 's'}.`;
          return;
        }
      }

      // Save websites
      if (liveChanges || this.websitesChanged) {
        await this.action(
          'profiles/updateActiveProfile',
          Profile.toPatch({
            website: null,
            websites: toNonEmptyStringArray([this.website1, this.website2, this.website3]),
          })
        );
      }

      // Save portfolio items
      if (!isUploading && (liveChanges || this.itemsChanged)) {
        await this.action('portfolios/updateMyPortfolio', portfolio);
        this.addedItems.length = 0;
        this.removedItems.length = 0;
        this.itemsReordered = false;
      }

      if (!this.liveChanges) {
        this.model = this.convertModel(this.$activeUser);
      }
      this.save();
    },
  },
  beforeDestroy() {
    const { portfolio } = this;
    if (portfolio && this.addedItems.length && !this.liveChanges && !this.isUploading) {
      for (const item of portfolio) {
        URL.revokeObjectURL(item.url);
      }
    }
  },
  watch: {
    'profile.websites': {
      /**
       * @param {(URL | null)[]} websites
       */
      handler(websites) {
        this.website1 = websites[0] || null;
        this.website2 = websites[1] || null;
        this.website3 = websites[2] || null;
      },
      immediate: true,
    },
  },
};
</script>
